import React, { useState, useEffect, useMemo } from 'react';
import { LysepAPI } from '../../contexts/ApiContext';
import DashboardWidget from '../ui/DashboardWidget';
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * SDUI Primitive Components
 */

const HeroSection = ({ title, description, icon, accentColor = '#fd7000' }) => {
    const Icon = Icons[icon?.charAt(0).toUpperCase() + icon?.slice(1)] || Icons.Zap;
    return (
        <div className="flex flex-col md:flex-row items-center gap-8 py-4 relative group">
            {/* Background Glow */}
            <div
                className="absolute -left-4 top-1/2 -translate-y-1/2 w-32 h-32 blur-[60px] opacity-20 pointer-events-none"
                style={{ backgroundColor: accentColor }}
            />

            <div className="relative">
                <div
                    className="w-20 h-20 rounded-[2rem] flex items-center justify-center shrink-0 shadow-2xl transition-transform duration-500 group-hover:scale-110"
                    style={{
                        backgroundColor: `${accentColor}15`,
                        border: `1px solid ${accentColor}30`,
                        boxShadow: `0 10px 30px -10px ${accentColor}40`
                    }}
                >
                    <Icon size={36} style={{ color: accentColor }} strokeWidth={1.5} />
                </div>
                {/* Decorative dot */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-4 border-[#0f1115] bg-green-500" />
            </div>

            <div className="text-center md:text-left flex-1">
                <h2 className="text-3xl font-bold text-white mb-3 tracking-tight leading-tight">
                    {title}
                </h2>
                <p className="text-gray-400 leading-relaxed text-sm max-w-xl font-medium opacity-80">
                    {description}
                </p>
            </div>
        </div>
    );
};

const StatList = ({ items, horizontal }) => {
    return (
        <div className={horizontal ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-4"}>
            {items.map((item, idx) => {
                const Icon = Icons[item.icon?.charAt(0).toUpperCase() + item.icon?.slice(1)] || Icons.Server;

                if (horizontal) {
                    return (
                        <div key={idx} className="group relative p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.1] hover:bg-white/[0.04] transition-all duration-300">
                            <div className="flex items-center gap-3 mb-2 text-gray-400 group-hover:text-gray-300 transition-colors">
                                <div className="p-2 rounded-xl bg-white/[0.03]">
                                    <Icon size={18} strokeWidth={2} />
                                </div>
                                <span className="text-xs font-bold uppercase tracking-widest">{item.label}</span>
                            </div>
                            <div className="text-2xl font-black text-white tracking-tighter">
                                {item.value}
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={idx} className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.01] hover:bg-white/[0.03] transition-colors group">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-white/[0.03] group-hover:scale-110 transition-transform">
                                <Icon size={18} className={item.color || "text-gray-400"} />
                            </div>
                            <span className="text-sm font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">{item.label}</span>
                        </div>
                        <span className="text-lg font-black text-white">{item.value}</span>
                    </div>
                );
            })}
        </div>
    );
};

const ActionButton = ({ label, action, icon, variant }) => {
    const navigate = useNavigate();
    const Icon = Icons[icon?.charAt(0).toUpperCase() + icon?.slice(1)];

    const baseStyles = "mt-6 relative overflow-hidden px-6 py-3 rounded-xl font-bold text-xs tracking-widest uppercase transition-all duration-500 active:scale-95 group flex items-center gap-3";
    const variants = {
        primary: "bg-[#fd7000] text-white shadow-[0_10px_20px_-5px_rgba(253,112,0,0.4)] hover:shadow-[0_15px_30px_-5px_rgba(253,112,0,0.6)] hover:-translate-y-1",
        outline: "bg-white/[0.03] border border-white/[0.08] text-white hover:bg-white/[0.08] hover:border-white/[0.15]"
    };

    return (
        <button
            onClick={() => navigate(action)}
            className={`${baseStyles} ${variants[variant] || variants.outline}`}
        >
            <span className="relative z-10">{label}</span>
            {Icon && <Icon size={16} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />}

            {/* Gloss effect for primary */}
            {variant === 'primary' && (
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-50" />
            )}
        </button>
    );
};

const ProgressBar = ({ label, value, color = '#fd7000', subtitle }) => (
    <div className="mt-6 space-y-2.5">
        <div className="flex justify-between items-end">
            <div className="flex flex-col">
                <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-0.5">{label}</span>
                {subtitle && <span className="text-[12px] font-semibold text-gray-400/80 tracking-tight">{subtitle}</span>}
            </div>
            <div className="flex items-baseline gap-0.5">
                <span className="text-2xl font-black text-white tracking-tighter tabular-nums">{value}</span>
                <span className="text-xs font-bold text-gray-500 tracking-widest">%</span>
            </div>
        </div>
        <div className="relative">
            {/* Track Background */}
            <div className="w-full h-1.5 bg-white/[0.03] rounded-full overflow-hidden border border-white/[0.05]">
                {/* Glow layer */}
                <div
                    className="h-full rounded-full transition-all duration-1000 ease-out relative"
                    style={{
                        width: `${value}%`,
                        background: `linear-gradient(90deg, ${color}CC, ${color})`,
                        boxShadow: `0 0 15px -1px ${color}30`,
                    }}
                >
                    {/* Glass highlight */}
                    <div className="absolute inset-x-0 top-0 h-[35%] bg-white/25 rounded-full" />

                    {/* Subtle scanning effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-shimmer" style={{ backgroundSize: '150% 100%' }} />
                </div>
            </div>

            {/* Soft Ambient Glow */}
            <div
                className="absolute -inset-1 blur-xl opacity-[0.07] pointer-events-none transition-all duration-1000"
                style={{ width: `${value}%`, backgroundColor: color }}
            />
        </div>
    </div>
);

const StatusIndicator = ({ label, status, color }) => {
    const colorClasses = {
        green: "bg-green-500 text-green-500 border-green-500/20",
        red: "bg-red-500 text-red-500 border-red-500/20",
        blue: "bg-blue-500 text-blue-500 border-blue-500/20"
    };
    const activeClass = colorClasses[color] || colorClasses.green;

    return (
        <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] transition-all cursor-default group">
            <span className="text-sm font-semibold text-gray-400 group-hover:text-gray-300 transition-colors uppercase tracking-tight">{label}</span>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${activeClass.split(' ')[2]} bg-white/[0.02]`}>
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${activeClass.split(' ')[0]}`} />
                <span className={`text-[10px] font-black uppercase tracking-widest ${activeClass.split(' ')[1]}`}>{status}</span>
            </div>
        </div>
    );
};

const PrimitiveRenderer = ({ type, props }) => {
    switch (type) {
        case 'hero': return <HeroSection {...props} />;
        case 'stat_list': return <StatList {...props} />;
        case 'action_button': return <ActionButton {...props} />;
        case 'progress_bar': return <ProgressBar {...props} />;
        case 'status_indicator': return <StatusIndicator {...props} />;
        case 'text': return <p className={`${props.className} font-medium`}>{props.content}</p>;
        case 'divider': return <div className="my-6 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />;
        default: return null;
    }
};

const WidgetRenderer = () => {
    const [widgets, setWidgets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWidgets = async () => {
            try {
                const resp = await LysepAPI.get('/api/dashboard/widgets');
                if (resp.data.state) {
                    setWidgets(resp.data.data);
                }
            } catch (err) {
                console.error("SDUI Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchWidgets();
    }, []);

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map(i => <DashboardWidget key={i} isLoading={true} />)}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {widgets.map(widget => {
                const Icon = Icons[widget.icon];
                return (
                    <DashboardWidget
                        key={widget.id}
                        title={widget.title}
                        className={
                            widget.size === 'full' ? 'lg:col-span-3' :
                                widget.size === 'large' ? 'lg:col-span-2' : ''
                        }
                        icon={Icon ? <Icon /> : null}
                    >
                        <div className="flex flex-col">
                            {widget.content.map((primitive, pIdx) => (
                                <PrimitiveRenderer key={pIdx} {...primitive} />
                            ))}
                        </div>
                    </DashboardWidget>
                );
            })}
        </div>
    );
};

export default WidgetRenderer;
